<script setup lang="ts">
// Ville du lieu (§10) : « Saint-Denis, La Réunion », pas la ville seule.
import { villeCourteLieu } from "#shared/utils/lieuPublic";

type ModalSection = "campaigns" | "events" | "memberships" | "posts";
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ModalItem = Record<string, any>;

const props = defineProps<{
  open: ModalSection | null;
  items: ModalItem[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  // Helpers injectés depuis le parent (déjà liés au assetsUrl)
  getImageUrl: (fileId: string | null, width?: number, height?: number) => string | null;
  formatAmount: (amount: number) => string;
  formatDate: (dateStr: string | null) => string | null;
  stripHtml: (html: string) => string;
}>();

defineEmits<{
  close: [];
  loadMore: [];
  openPost: [post: ModalItem];
}>();

const { t } = useI18n();
const { formatCurrency } = useFormatLangue();

const titleMap = computed<Record<ModalSection, string>>(() => ({
  campaigns: t("association.voirPlusModal.toutesLesCagnottes"),
  events: t("association.voirPlusModal.tousLesEvenements"),
  memberships: t("association.voirPlusModal.toutesLesAdhesions"),
  posts: t("association.voirPlusModal.toutesLesActualites"),
}));

// Verrouille le scroll de la PAGE derrière tant que la modale est ouverte
// (verrou PARTAGÉ à compteur — survit à l'empilement avec la modale article).
// Complété par overscroll-behavior:contain sur .modal-body. Karim 2026-08-05.
useBodyScrollLock().bindToRef(() => props.open);
</script>

<template>
  <Teleport to="body">
    <Transition name="modal-fade">
      <div v-if="open" class="modal-overlay" @click.self="$emit('close')">
        <div class="modal-panel">
          <!-- Header -->
          <div class="modal-header">
            <h2 class="text-lg font-bold text-gray-900">{{ titleMap[open] }}</h2>
            <button class="modal-close" @click="$emit('close')">
              <UIcon name="i-lucide-x" class="size-5" />
            </button>
          </div>

          <!-- Content -->
          <div class="modal-body">
            <!-- Skeleton initial -->
            <div v-if="loading" class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div v-for="i in 6" :key="i" class="bg-gray-100 rounded-2xl h-48 animate-pulse" />
            </div>

            <!-- Cagnottes -->
            <div v-else-if="open === 'campaigns'" class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <NuxtLink
                v-for="campaign in items" :key="campaign.id"
                :to="`/c/${campaign.slug}`" target="_blank"
                class="group bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300 no-underline flex flex-col"
              >
                <div class="relative h-36 overflow-hidden shrink-0 bg-gray-100">
                  <template v-if="campaign.image">
                    <img :src="getImageUrl(campaign.image)!" class="absolute inset-0 w-full h-full object-cover blur-xl scale-110 opacity-60" alt="" loading="lazy" />
                    <img :src="getImageUrl(campaign.image)!" :alt="campaign.title" class="relative w-full h-full object-contain group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  </template>
                  <div v-else class="w-full h-full bg-gray-100 flex items-center justify-center"><UIcon name="i-lucide-image" class="size-8 text-gray-300" /></div>
                </div>
                <!-- Couleurs alignées sur les cartes de la page (bleu main/main2),
                     plus le vert hérité d'un vieux design. Karim 2026-08-05. -->
                <div class="p-4 flex flex-col flex-1">
                  <div class="flex-1 mb-2">
                    <h3 class="font-semibold text-gray-900 text-sm line-clamp-2 group-hover:text-main2 transition-colors">{{ campaign.title }}</h3>
                    <p v-if="campaign.description" class="text-xs text-gray-500 line-clamp-2 mt-1">{{ stripHtml(campaign.description) }}</p>
                  </div>
                  <div v-if="campaign.goal_amount > 0" class="space-y-1.5">
                    <div class="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                      <div class="h-full bg-gradient-to-br from-main to-main2 rounded-full" :style="{ width: `${Math.min(campaign.percent, 100)}%` }" />
                    </div>
                    <div class="flex justify-between text-xs text-gray-500">
                      <span class="font-semibold text-main2">{{ formatAmount(campaign.raised) }}</span>
                      <span>{{ campaign.percent }}%</span>
                    </div>
                  </div>
                  <div v-else class="text-xs text-gray-500"><span class="font-semibold text-main2">{{ formatAmount(campaign.raised) }}</span> {{ t("association.voirPlusModal.collectes") }}</div>
                </div>
              </NuxtLink>
            </div>

            <!-- Événements -->
            <div v-else-if="open === 'events'" class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <NuxtLink
                v-for="event in items" :key="event.id"
                :to="`/b/${event.slug}`" target="_blank"
                class="group bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300 no-underline"
              >
                <div class="relative h-36 overflow-hidden rounded-t-2xl">
                  <img v-if="event.cover_image" :src="getImageUrl(event.cover_image)!" :alt="event.title" class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  <div v-else class="w-full h-full bg-purple-50 flex items-center justify-center"><UIcon name="i-lucide-calendar" class="size-8 text-purple-300" /></div>
                </div>
                <div class="p-4">
                  <h3 class="font-semibold text-gray-900 text-sm mb-1 line-clamp-2 group-hover:text-purple-600 transition-colors">{{ event.title }}</h3>
                  <p v-if="event.description" class="text-xs text-gray-500 line-clamp-2 mb-2">{{ stripHtml(event.description) }}</p>
                  <div class="flex items-center gap-3 text-xs text-gray-500">
                    <div v-if="event.start_date" class="flex items-center gap-1"><UIcon name="i-lucide-clock" class="size-3.5" /><span>{{ formatDate(event.start_date) }}</span></div>
                    <div v-if="villeCourteLieu(event)" class="flex items-center gap-1"><UIcon name="i-lucide-map-pin" class="size-3.5" /><span>{{ villeCourteLieu(event) }}</span></div>
                  </div>
                </div>
              </NuxtLink>
            </div>

            <!-- Adhésions -->
            <div v-else-if="open === 'memberships'" class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <NuxtLink
                v-for="plan in items" :key="plan.id"
                :to="`/a/${plan.slug}`" target="_blank"
                class="group bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300 no-underline"
              >
                <div class="relative h-36 overflow-hidden rounded-t-2xl">
                  <img v-if="plan.cover_image" :src="getImageUrl(plan.cover_image)!" :alt="plan.title" class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  <div v-else class="w-full h-full bg-teal-50 flex items-center justify-center"><UIcon name="i-lucide-id-card" class="size-8 text-teal-300" /></div>
                </div>
                <div class="p-4">
                  <h3 class="font-semibold text-gray-900 text-sm mb-1 line-clamp-2 group-hover:text-teal-600 transition-colors">{{ plan.title }}</h3>
                  <p v-if="plan.description" class="text-xs text-gray-500 line-clamp-2 mb-2">{{ stripHtml(plan.description) }}</p>
                  <div class="text-xs text-gray-500">
                    <span class="font-medium text-teal-600">{{ Number(plan.price) > 0 ? formatCurrency(Number(plan.price)) : t('association.voirPlusModal.gratuit') }}</span>
                  </div>
                </div>
              </NuxtLink>
            </div>

            <!-- Actualités : chaque carte ouvre l'article complet -->
            <div v-else-if="open === 'posts'" class="space-y-4">
              <button
                v-for="post in items"
                :key="post.id"
                type="button"
                class="post-card block w-full text-left bg-white rounded-2xl border border-gray-100 overflow-hidden"
                @click="$emit('openPost', post)"
              >
                <img v-if="post.image" :src="getImageUrl(post.image, 600, 300)!" :alt="post.title" class="w-full h-40 object-cover" loading="lazy" />
                <div class="p-4">
                  <div class="flex items-center gap-2 text-xs text-gray-400 mb-1"><UIcon name="i-lucide-calendar" class="size-3.5" /><span>{{ formatDate(post.date_created) }}</span></div>
                  <h3 class="font-bold text-gray-900 mb-1">{{ post.title }}</h3>
                  <!-- eslint-disable vue/no-v-html -->
                  <!-- Reason: post.content sanitizé côté backend Donoor -->
                  <div v-if="post.content" class="prose prose-sm prose-gray max-w-none line-clamp-3" v-html="post.content" />
                  <!-- eslint-enable vue/no-v-html -->
                  <span class="post-card-cta text-sm font-semibold text-main2 mt-2 inline-flex items-center gap-1">{{ t("association.voirPlusModal.lireLaSuite") }} <UIcon name="i-lucide-arrow-right" class="size-4" /></span>
                </div>
              </button>
            </div>

            <!-- Charger plus -->
            <div v-if="!loading && hasMore" class="flex justify-center mt-6">
              <button :disabled="loadingMore" class="voir-plus-btn" @click="$emit('loadMore')">
                <UIcon v-if="loadingMore" name="i-lucide-loader-2" class="size-4 animate-spin" />
                <template v-else>{{ t("association.voirPlusModal.chargerPlus") }} <UIcon name="i-lucide-chevron-down" class="size-4" /></template>
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<!--
  Styles non-scoped : la modale est teleport-ée dans <body> donc hors
  du scope CSS du composant. Garder ces classes globales (préfixe `modal-`
  spécifique) évite les manip :deep().
-->
<style>
.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 200;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 0;
}
@media (min-width: 640px) {
  .modal-overlay {
    align-items: center;
    padding: 1.5rem;
  }
}

.modal-panel {
  background: #f8f9fa;
  width: 100%;
  max-width: 760px;
  max-height: 90vh;
  border-radius: 24px 24px 0 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
@media (min-width: 640px) {
  .modal-panel {
    border-radius: 24px;
  }
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  background: white;
  flex-shrink: 0;
}

.modal-close {
  width: 2rem;
  height: 2rem;
  border-radius: 9999px;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;
  color: #6b7280;
  transition: background 0.2s;
}
.modal-close:hover { background: #e5e7eb; }

.modal-body {
  overflow-y: auto;
  padding: 1.5rem;
  flex: 1;
  /* Le scroll molette/tactile reste DANS la modale : pas de chaînage vers la
     page derrière quand on atteint le bout de la liste. Karim 2026-08-05. */
  overscroll-behavior: contain;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.25s ease;
}
.modal-fade-enter-active .modal-panel,
.modal-fade-leave-active .modal-panel {
  transition: transform 0.25s ease;
}
.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}
.modal-fade-enter-from .modal-panel,
.modal-fade-leave-to .modal-panel {
  transform: translateY(20px);
}

/* Cartes actualité dans la modale : cliquables → ouvrent l'article complet. */
.modal-body .post-card {
  cursor: pointer;
  transition: box-shadow 0.25s, transform 0.25s;
}
.modal-body .post-card:hover {
  box-shadow: 0 12px 36px -12px rgba(19, 20, 69, 0.18);
  transform: translateY(-2px);
}
.modal-body .post-card:hover .post-card-cta {
  text-decoration: underline;
}

/* Bouton "Charger plus" interne à la modal — identique à voir-plus-btn parent */
.modal-body .voir-plus-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1.25rem;
  border-radius: 9999px;
  background: white;
  border: 1px solid #e5e7eb;
  color: #374151;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}
.modal-body .voir-plus-btn:hover {
  background: #f9fafb;
  border-color: #d1d5db;
}
.modal-body .voir-plus-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
